import {
  TypeLink_internal,
  TypeProduct,
  TypeProductFields,
  TypeShipment,
} from './generated-types';
import {
  CONTENT_GROUP_ACCOUNT_PAGE,
  CONTENT_TYPE_ACCOUNT_PAGE,
  CONTENT_TYPE_COLLECTION_PAGE,
  CONTENT_TYPE_PAGE,
  CONTENT_TYPE_PRODUCT_PAGE,
  CONTENT_TYPE_REFERRAL_PAGE,
  ContentfulPage,
  getContentGroup,
  getContentType,
  getEntryLocale,
} from '@/core/constants';
import { ShopifyProduct } from '@/lib/shopify';
import { PathConfig } from '@/utils/pathBuilder';

export type ContentEntry<T> = Omit<T, 'toPlainObject' | 'update'>;

export interface ExtendedProductFields extends TypeProductFields {
  shopifyProduct?: ShopifyProduct;
}

export interface ExtendedProduct extends TypeProduct {
  fields: ExtendedProductFields;
}

export interface ExtendedProductFieldsWithShipment
  extends ExtendedProductFields {
  shipment?: TypeShipment;
}

export interface ExtendedProductWithShipment extends ExtendedProduct {
  fields: ExtendedProductFieldsWithShipment;
}

export const defaultCacheRevalidation = 90; // 1.5 minutes

export const getAccountPageRoute = (
  contentGroup: string,
  slug: string,
  anchor = '',
  queryString: string,
): string => {
  const {
    COURSE_STREAMING_PAGE,
    READING_SKILL_SET_STREAMING_PAGE,
    ADD_CHILD_PAGE,
    EDIT_CHILD_PAGE,
    SUBSCRIBER_SHOP_FAMILY_PAGE,
    SUBSCRIBER_SHOP_WELCOME_PAGE,
  } = CONTENT_GROUP_ACCOUNT_PAGE;

  switch (contentGroup) {
    case COURSE_STREAMING_PAGE:
      return `/account/my-course-packs/${slug}${queryString}${anchor}`;
    case READING_SKILL_SET_STREAMING_PAGE:
      return `/account/reading-skill-set/${slug}${queryString}${anchor}`;
    case ADD_CHILD_PAGE:
    case EDIT_CHILD_PAGE:
      return `/account/settings/${slug}${queryString}${anchor}`;
    case SUBSCRIBER_SHOP_FAMILY_PAGE:
    case SUBSCRIBER_SHOP_WELCOME_PAGE:
      return `/account/subscriber-shop/${slug}${queryString}${anchor}`;
    default:
      return `/account/${slug}${queryString}${anchor}`;
  }
};

export const getRouteForPage = (
  contentTypeId: string,
  contentGroupId: string,
  slug: string,
  anchor = '',
  queryString = '',
): string => {
  if (slug === 'home') {
    slug = '/';
  }

  const { PAGE } = CONTENT_TYPE_PAGE;
  const { REFERRAL_PAGE } = CONTENT_TYPE_REFERRAL_PAGE;
  const { ACCOUNT_PAGE } = CONTENT_TYPE_ACCOUNT_PAGE;
  const { COLLECTION_PAGE } = CONTENT_TYPE_COLLECTION_PAGE;
  const {
    PRODUCT_PAGE,
    PLAY_KIT_PAGE,
    PLAY_KITS_LANDING_PAGE,
    PRODUCT_LANDING_PAGE,
    AGE_BASED_PLAY_KITS_LANDING_PAGE,
  } = CONTENT_TYPE_PRODUCT_PAGE;

  const formattedQueryString = queryString ? `?${queryString}` : '';

  switch (contentTypeId) {
    case AGE_BASED_PLAY_KITS_LANDING_PAGE:
      return `/products/the-play-kits/${slug}${anchor}`;
    case PLAY_KITS_LANDING_PAGE:
    case PLAY_KIT_PAGE:
    case PRODUCT_PAGE:
    case PRODUCT_LANDING_PAGE:
      return `/products/${slug}${anchor}${formattedQueryString}`;
    case PAGE:
    case REFERRAL_PAGE:
      return `/pages/${slug}${anchor}`;
    case COLLECTION_PAGE:
      return `/collections/${slug}${anchor}`;
    case ACCOUNT_PAGE:
      return getAccountPageRoute(contentGroupId, slug, anchor, queryString);
    default:
      // This also takes care of the case of an internal link that merely targets an anchor on the current page.
      return `${slug}${anchor}`;
  }
};

export const slugBuilder = (
  link: TypeLink_internal,
  queryString?: string,
): string => {
  // For statically generated pages, one or more of the warnings below will surface in build logs along the appropriate error if an Internal Link entry in Contentful is invalid and makes the build fail.
  if (!link) {
    console.warn('Invalid link. Link is falsy.');
    return '/';
  }

  if (!link.fields) {
    console.warn('Invalid link. No fields found.', { link });
    return '/';
  }

  const linkContent = link.fields?.linkContent;
  const destinationId = link.fields?.destinationId;

  if (!linkContent) {
    console.warn(
      `Invalid link (linkText: "${link.fields?.linkText}"). No linkContent field found.`,
      { linkFields: link.fields },
    );
  }

  return getRouteForPage(
    getContentType(linkContent),
    getContentGroup(linkContent),
    linkContent?.fields?.slug || '',
    destinationId ? `#${destinationId}` : '',
    queryString || '',
  );
};

export function getAuxillaryLocale(locale: string) {
  const supportedLocales = new Set(['nl', 'es', 'de', 'fr']);
  return supportedLocales.has(locale) ? locale : '';
}

export function buildCanonicalUrl(page: ContentfulPage) {
  const path = getRouteForPage(
    getContentType(page),
    getContentGroup(page),
    page?.fields?.slug || '',
  );

  const locale = getEntryLocale(page);
  const auxillaryLocale = getAuxillaryLocale(locale);
  const localeString = auxillaryLocale ? `/${auxillaryLocale}` : '';

  const base = `https://${process.env.NEXT_PUBLIC_VANITY_DOMAIN}`;

  let buildedCanonicalUrl = new URL(`${localeString}${path}`, base).toString();

  buildedCanonicalUrl = buildedCanonicalUrl.replace(/[a-zA-Z-]*variant-/g, '');

  return buildedCanonicalUrl;
}

// The Product content type has four Shopify product fields, one for each store.
// This function returns the name of the field for the current store.
export function shopifyProductField(): string {
  const storeDomain = process.env.NEXT_PUBLIC_SHOPIFY_STORE_DOMAIN;

  switch (storeDomain) {
    case 'lovevery.myshopify.com':
      return 'shopifyProductUs';
    case 'lovevery-uk-prod.myshopify.com':
      return 'shopifyProductUk';
    case 'lovevery-au-prod.myshopify.com':
      return 'shopifyProductAu';
    case 'lovevery-international-prod.myshopify.com':
      return 'shopifyProductEu';
    case 'lovevery-kits.myshopify.com':
      return 'shopifyProductUsStaging';
    case 'lovevery-international-staging.myshopify.com':
      return 'shopifyProductEuStaging';
    case 'lovevery-uk-staging.myshopify.com':
      return 'shopifyProductUkStaging';
    case 'lovevery-au-staging.myshopify.com':
      return 'shopifyProductAuStaging';
    case 'lovevery-demo.myshopify.com':
      return 'shopifyProductDemo';
    default:
      return 'shopifyProductUs';
  }
}

const SHOPIFY_PRODUCT_FIELD_MAPPING = Object.freeze({
  shopifyProductUsStaging: 'shopifyProductUs',
  shopifyProductUkStaging: 'shopifyProductUk',
  shopifyProductAuStaging: 'shopifyProductAu',
  shopifyProductEuStaging: 'shopifyProductEu',
  shopifyProductDemo: 'shopifyProductUs',
});

// Maps a Shopify product field name from staging to production environment
export function shopifyProductFieldMapping(productIdKey: string): string {
  return (
    SHOPIFY_PRODUCT_FIELD_MAPPING[
      productIdKey as keyof typeof SHOPIFY_PRODUCT_FIELD_MAPPING
    ] || productIdKey
  );
}

export const shouldIncludeStaticPath = (path: PathConfig) => {
  switch (path.params.slug) {
    case 'refer-a-friend':
      return false;
    default:
      return true;
  }
};
